import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Subcomponent: Message Card
const MessageCard = ({ message }) => (
  <div className="bg-white border-2 gold-border rounded-lg p-6 hover:shadow-lg transition-shadow">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <p className="text-sm text-gray-600 mb-1">Name</p>
        <p className="font-semibold text-gray-900">{message.name}</p>
      </div>
      <div>
        <p className="text-sm text-gray-600 mb-1">Email</p>
        <a 
          href={`mailto:${message.email}`} 
          className="font-semibold text-primary hover:underline"
        >
          {message.email}
        </a>
      </div>
      {message.phone && (
        <div>
          <p className="text-sm text-gray-600 mb-1">Phone</p>
          <p className="font-semibold text-gray-900">{message.phone}</p>
        </div>
      )}
      {message.company && (
        <div>
          <p className="text-sm text-gray-600 mb-1">Company</p>
          <p className="font-semibold text-gray-900">{message.company}</p>
        </div>
      )}
    </div>
    <div className="mb-4">
      <p className="text-sm text-gray-600 mb-1">Message</p>
      <p className="text-gray-900 whitespace-pre-wrap">{message.message}</p>
    </div>
    <div>
      <p className="text-xs text-gray-500">
        Received: {new Date(message.timestamp).toLocaleString()}
      </p>
    </div>
  </div>
);

// Subcomponent: Admin Header
const AdminHeader = ({ onLogout }) => (
  <div className="flex justify-between items-center mb-8">
    <div>
      <h1 className="text-4xl font-bold text-primary mb-2">
        Admin Dashboard
      </h1>
      <div className="w-24 h-1 bg-gold"></div>
    </div>
    <button
      onClick={onLogout}
      className="px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-semibold flex items-center"
    >
      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
      </svg>
      Logout
    </button>
  </div>
);

// Main Admin Component
const Admin = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchMessages = useCallback(async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(
        `${backendUrl}/api/contact`,
        { withCredentials: true } // Important: send cookies
      );
      const sortedMessages = response.data.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setMessages(sortedMessages);
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) {
        // Not authenticated, redirect to login
        navigate('/login');
      } else {
        setError('Failed to load messages');
        setLoading(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleLogout = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${backendUrl}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (err) {
      // Ignore logout errors
    } finally {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="content-section">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-section">
        <div className="container mx-auto px-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section">
      <div className="container mx-auto px-4">
        <div className="glass-card rounded-lg p-8 md:p-12">
          <AdminHeader onLogout={handleLogout} />

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-primary mb-4">
              Contact Messages ({messages.length})
            </h2>
          </div>

          {messages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No messages yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {messages.map((message) => (
                <MessageCard key={message.id} message={message} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
